import './styles/DRIFTALARM_COLOR_PALETTE.css';
import './styles/DRIFTALARM_BASE_STYLES.css';
import './styles/DRIFTALARM_ANIMATIONS.css';
import './styles/DRIFTALARM_COMPONENTS.css';
import './styles/commandCenterTheme.css';
import './styles/themeVariations.css';
import './styles/cleanModern.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { HelmetProvider } from 'react-helmet-async';
import { msalConfig } from './config/authConfig';
import App from './App';
import { track as trackTelemetry } from './services/telemetryService';
import './index.css';

// Detect if we're running in react-snap pre-rendering mode
// react-snap uses HeadlessChrome and we should skip MSAL init
const isPrerendering = navigator.userAgent.includes('HeadlessChrome') ||
                       navigator.userAgent.includes('ReactSnap');

// Helper function to render the app (handles both hydration and fresh render)
function renderApp(msalInstance) {
  const rootElement = document.getElementById('root');
  const appContent = (
    <React.StrictMode>
      <HelmetProvider>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </HelmetProvider>
    </React.StrictMode>
  );

  // Check if we have pre-rendered content (react-snap)
  // If root has children, hydrate; otherwise, create fresh root
  if (rootElement.hasChildNodes()) {
    ReactDOM.hydrateRoot(rootElement, appContent);
  } else {
    const root = ReactDOM.createRoot(rootElement);
    root.render(appContent);
  }
}

if (isPrerendering) {
  // During pre-rendering, create a minimal MSAL instance and render immediately
  // This allows react-snap to capture the static HTML without waiting for auth
  const msalInstance = new PublicClientApplication(msalConfig);
  renderApp(msalInstance);
} else {
  // Normal runtime: Initialize MSAL and handle redirects
  const msalInstance = new PublicClientApplication(msalConfig);

  // Make MSAL instance available globally for API calls
  window.msalInstance = msalInstance;

  // Handle redirect promise BEFORE rendering
  // This is critical for the redirect flow to work properly
  msalInstance.initialize().then(() => {
    // Handle the redirect from CIAM login
    msalInstance.handleRedirectPromise()
      .then((response) => {
        if (response) {
          // User just logged in via redirect
          console.log('Login successful via redirect:', response);

          // PRD-151 WP2: best-effort login telemetry. `response` truthy means a
          // fresh login just completed. track() is a no-op during pre-render.
          try { trackTelemetry('login', {}); } catch (e) { /* best-effort */ }

          // PRD-151 WP3: tie GA4 sessions to the OPAQUE CIAM oid (localAccountId)
          // — never email/IP/PII. Set once on fresh login; persists for subsequent
          // gtag page_view events. (Returning cached sessions with no redirect
          // response won't re-set it — acceptable per PRD "optional user_id".)
          try {
            const oid = response.account && response.account.localAccountId;
            if (oid && window.gtag) window.gtag('set', { user_id: oid });
          } catch (e) { /* best-effort */ }

          // Get the intended destination (stored before redirect)
          const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/dashboard';
          sessionStorage.removeItem('redirectAfterLogin');

          // Store the path to redirect to after React loads
          sessionStorage.setItem('postLoginRedirect', redirectPath);
        }

        // Now render the app
        renderApp(msalInstance);
      })
      .catch((error) => {
        console.error('Error handling redirect:', error);

        // Still render the app even if there's an error
        renderApp(msalInstance);
      });
  }).catch((error) => {
    console.error('MSAL initialization failed:', error);

    // Still render the app so the UI is functional (auth features will be degraded)
    renderApp(msalInstance);
  });
}